import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import PasswordResetModal from '../../components/PasswordResetModal';
import { Users, Plus, Edit2, Trash2, X, Eye, EyeOff, Copy, Check, KeyRound, BarChart3, CreditCard, Calendar, AlertTriangle, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    role: 'user', 
    rate_limit: 30, 
    rate_limit_per_minute: 0,
    rate_limit_per_hour: 0,
    rate_limit_per_day: 0,
    status: 'active' 
  });
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  
  // Password Reset Modal State
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetPasswordUserEmail, setResetPasswordUserEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resettingPasswordForUser, setResettingPasswordForUser] = useState(null);

  // Rate Limit Status Modal State
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  const [rateLimitLoading, setRateLimitLoading] = useState(false);

  // Subscription Modal State
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptionForm, setSubscriptionForm] = useState({ plan: 'basic', duration_days: 30 });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUsers();
    fetchSubscriptionPlans();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => 
        user.email.toLowerCase().includes(query) ||
        (user.subscription_plan && user.subscription_plan.toLowerCase().includes(query)) ||
        (user.role && user.role.toLowerCase().includes(query)) ||
        (user.status && user.status.toLowerCase().includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const fetchSubscriptionPlans = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/subscription/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setFormData({ 
      email: '', 
      password: 'Password@123', 
      role: 'user', 
      rate_limit: 30, 
      rate_limit_per_minute: 0,
      rate_limit_per_hour: 0,
      rate_limit_per_day: 0,
      status: 'active' 
    });
    setShowModal(true);
  };

  const handleViewRateLimitStatus = async (user) => {
    setRateLimitLoading(true);
    setShowRateLimitModal(true);
    setRateLimitStatus(null);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users/${user.id}/rate-limit-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRateLimitStatus(response.data);
    } catch (error) {
      console.error('Error fetching rate limit status:', error);
      setRateLimitStatus({ error: 'Failed to fetch rate limit status' });
    } finally {
      setRateLimitLoading(false);
    }
  };

  const handleEdit = (user) => {
    setModalType('edit');
    setSelectedUser(user);
    setFormData({ 
      email: user.email, 
      role: user.role || 'user', 
      rate_limit: user.rate_limit || 30,
      rate_limit_per_minute: user.rate_limit_per_minute || 0,
      rate_limit_per_hour: user.rate_limit_per_hour || 0,
      rate_limit_per_day: user.rate_limit_per_day || 0,
      status: user.status === 'suspended' ? 'deactive' : (user.status || 'active')
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (modalType === 'create') {
        await axios.post(`${API}/admin/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API}/admin/users/${selectedUser.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userEmail}?`)) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleStatus = async (user) => {
    const currentStatus = user.status === 'suspended' ? 'deactive' : (user.status || 'active');
    const newStatus = currentStatus === 'active' ? 'deactive' : 'active';
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/users/${user.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      alert('Status update failed');
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const copyPassword = (password, userId) => {
    if (password) {
      navigator.clipboard.writeText(password);
      setCopiedId(userId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Handle Password Reset
  const handleResetPassword = async (user) => {
    if (!window.confirm(`Are you sure you want to reset password for "${user.email}"?`)) return;
    
    setResettingPasswordForUser(user.id);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/reset-password/${user.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemporaryPassword(response.data.temporary_password);
      setResetPasswordUserEmail(user.email);
      setShowPasswordResetModal(true);
      fetchUsers(); // Refresh to show updated status
    } catch (error) {
      alert(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setResettingPasswordForUser(null);
    }
  };

  const getDisplayStatus = (status) => {
    if (!status || status === 'suspended') return 'deactive';
    return status;
  };

  const getDisplayPassword = (user) => {
    // Show plain_password if available, otherwise show a placeholder
    return user.plain_password || 'N/A (created before update)';
  };

  // Subscription Management Functions
  const handleManageSubscription = (user) => {
    setSubscriptionUser(user);
    setSubscriptionForm({
      plan: user.subscription_plan || 'free',
      duration_days: 30
    });
    setSubscriptionMessage({ type: '', text: '' });
    setShowSubscriptionModal(true);
  };

  const handleActivateSubscription = async () => {
    setSubscriptionLoading(true);
    setSubscriptionMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/subscription/activate`, {
        user_id: subscriptionUser.id,
        plan: subscriptionForm.plan,
        duration_days: subscriptionForm.duration_days
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptionMessage({ type: 'success', text: response.data.message });
      fetchUsers();
      setTimeout(() => setShowSubscriptionModal(false), 1500);
    } catch (error) {
      setSubscriptionMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to activate subscription' });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleExtendSubscription = async () => {
    setSubscriptionLoading(true);
    setSubscriptionMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/subscription/extend`, {
        user_id: subscriptionUser.id,
        days: subscriptionForm.duration_days
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptionMessage({ type: 'success', text: response.data.message });
      fetchUsers();
      setTimeout(() => setShowSubscriptionModal(false), 1500);
    } catch (error) {
      setSubscriptionMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to extend subscription' });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? User will be downgraded to Free plan.')) return;
    
    setSubscriptionLoading(true);
    setSubscriptionMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/subscription/cancel`, {
        user_id: subscriptionUser.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptionMessage({ type: 'success', text: response.data.message });
      fetchUsers();
      setTimeout(() => setShowSubscriptionModal(false), 1500);
    } catch (error) {
      setSubscriptionMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to cancel subscription' });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const getSubscriptionBadge = (user) => {
    const status = user.subscription_status;
    if (!status) return { text: 'Free', color: '#64748b', bg: '#f1f5f9' };
    
    if (!status.is_active && status.plan !== 'free') {
      return { text: 'EXPIRED', color: '#991b1b', bg: '#fee2e2' };
    }
    
    switch (status.plan) {
      case 'free':
        return { text: 'Free', color: '#64748b', bg: '#f1f5f9' };
      case 'basic':
        return { text: 'Basic', color: '#1d4ed8', bg: '#dbeafe' };
      case 'pro':
        return { text: 'Pro', color: '#b45309', bg: '#fef3c7' };
      default:
        return { text: status.plan_name || status.plan, color: '#7c3aed', bg: '#ede9fe' };
    }
  };

  if (loading) {
    return <AdminLayout><div style={{ color: '#64748b', padding: '40px', textAlign: 'center' }}>Loading...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Users Management</h1>
          <p style={{ color: '#64748b', fontSize: '15px' }}>Manage all user accounts ({users.length} total{searchQuery && `, showing ${filteredUsers.length}`})</p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Search Box */}
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="user-search-input"
              style={{
                width: '220px',
                padding: '10px 36px 10px 38px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '2px'
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          
          <button
            onClick={handleCreate}
            data-testid="create-user-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
          >
            <Plus size={18} />
            Create User
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Email</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Password</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>API Key</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Role</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Subscription</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Rate Limit</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Created</th>
                <th style={{ padding: '16px', textAlign: 'right', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found'}
                  </td>
                </tr>
              ) : filteredUsers.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '16px', color: '#1e293b', fontWeight: '500', fontSize: '14px' }}>{user.email}</td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{ 
                        color: '#64748b', 
                        fontSize: '13px',
                        background: '#f1f5f9',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {showPasswords[user.id] ? getDisplayPassword(user) : '••••••••'}
                      </code>
                      <button
                        onClick={() => togglePasswordVisibility(user.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
                        title={showPasswords[user.id] ? 'Hide' : 'Show'}
                      >
                        {showPasswords[user.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      {user.plain_password && (
                        <button
                          onClick={() => copyPassword(user.plain_password, user.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === user.id ? '#10b981' : '#94a3b8', padding: '4px' }}
                          title="Copy"
                        >
                          {copiedId === user.id ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      )}
                    </div>
                  </td>
                  {/* API Key Column */}
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code style={{ 
                        color: '#6366f1', 
                        fontSize: '11px',
                        background: '#eef2ff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'Monaco, monospace'
                      }}>
                        {user.api_key ? `${user.api_key.substring(0, 12)}...` : 'N/A'}
                      </code>
                      {user.api_key && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(user.api_key);
                            setCopiedId(`api_${user.id}`);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === `api_${user.id}` ? '#10b981' : '#94a3b8', padding: '4px' }}
                          title="Copy API Key"
                        >
                          {copiedId === `api_${user.id}` ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ 
                      padding: '4px 12px', 
                      background: (user.role || 'user') === 'admin' ? '#667eea' : '#3b82f6', 
                      color: 'white', 
                      borderRadius: '12px', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <button
                      onClick={() => handleToggleStatus(user)}
                      style={{ 
                        padding: '4px 12px', 
                        background: getDisplayStatus(user.status) === 'active' ? '#dcfce7' : '#fee2e2', 
                        color: getDisplayStatus(user.status) === 'active' ? '#166534' : '#991b1b', 
                        borderRadius: '12px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        border: 'none',
                        cursor: 'pointer',
                        textTransform: 'capitalize'
                      }}
                      title={`Click to ${getDisplayStatus(user.status) === 'active' ? 'deactivate' : 'activate'}`}
                    >
                      {getDisplayStatus(user.status)}
                    </button>
                  </td>
                  <td style={{ padding: '16px' }}>
                    {(() => {
                      const badge = getSubscriptionBadge(user);
                      const status = user.subscription_status;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{
                            padding: '4px 10px',
                            background: badge.bg,
                            color: badge.color,
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'inline-block',
                            width: 'fit-content'
                          }}>
                            {badge.text}
                          </span>
                          {status?.days_remaining !== null && status?.days_remaining !== undefined && status.plan !== 'free' && (
                            <span style={{ 
                              fontSize: '11px', 
                              color: status.days_remaining <= 7 ? '#dc2626' : '#64748b'
                            }}>
                              {status.days_remaining > 0 ? `${status.days_remaining} days left` : 'Expired'}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '16px', color: '#64748b', fontSize: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span title="Per Minute">{user.rate_limit_per_minute === 0 ? '∞' : user.rate_limit_per_minute}/min</span>
                      <span title="Per Hour">{user.rate_limit_per_hour === 0 ? '∞' : user.rate_limit_per_hour}/hr</span>
                      <span title="Per Day">{user.rate_limit_per_day === 0 ? '∞' : user.rate_limit_per_day}/day</span>
                    </div>
                  </td>
                  <td style={{ padding: '16px', color: '#64748b', fontSize: '14px' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleManageSubscription(user)}
                        data-testid={`subscription-btn-${user.id}`}
                        style={{ 
                          padding: '8px', 
                          background: '#ede9fe', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          color: '#7c3aed'
                        }}
                        title="Manage Subscription"
                      >
                        <CreditCard size={16} />
                      </button>
                      <button
                        onClick={() => handleViewRateLimitStatus(user)}
                        data-testid={`rate-limit-status-btn-${user.id}`}
                        style={{ 
                          padding: '8px', 
                          background: '#fef3c7', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          color: '#d97706'
                        }}
                        title="View Rate Limit Status"
                      >
                        <BarChart3 size={16} />
                      </button>
                      <button
                        onClick={() => handleResetPassword(user)}
                        disabled={resettingPasswordForUser === user.id}
                        data-testid={`reset-password-btn-${user.id}`}
                        style={{ 
                          padding: '8px', 
                          background: resettingPasswordForUser === user.id ? '#e0e7ff' : '#dbeafe', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: resettingPasswordForUser === user.id ? 'wait' : 'pointer', 
                          color: '#3b82f6',
                          opacity: resettingPasswordForUser === user.id ? 0.7 : 1
                        }}
                        title="Reset Password"
                      >
                        <KeyRound size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(user)}
                        style={{ padding: '8px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#64748b' }}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      {(user.role || 'user') !== 'admin' && (
                        <button
                          onClick={() => handleDelete(user.id, user.email)}
                          style={{ padding: '8px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#ef4444' }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                {modalType === 'create' ? 'Create User' : 'Edit User'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={modalType === 'edit'}
                  style={{ width: '100%', padding: '12px', background: modalType === 'edit' ? '#f8fafc' : 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                />
              </div>

              {modalType === 'create' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Password</label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                  />
                  <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>This password will be visible in the users list</p>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                >
                  <option value="active">Active</option>
                  <option value="deactive">Deactive</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
                  Rate Limits <span style={{ fontSize: '12px', color: '#94a3b8' }}>(0 = Unlimited)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Minute</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_minute}
                      onChange={(e) => setFormData({...formData, rate_limit_per_minute: parseInt(e.target.value) || 0})}
                      min="0"
                      max="1000"
                      placeholder="0"
                      style={{ width: '100%', padding: '10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Hour</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_hour}
                      onChange={(e) => setFormData({...formData, rate_limit_per_hour: parseInt(e.target.value) || 0})}
                      min="0"
                      max="10000"
                      placeholder="0"
                      style={{ width: '100%', padding: '10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '12px' }}>Per Day</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_day}
                      onChange={(e) => setFormData({...formData, rate_limit_per_day: parseInt(e.target.value) || 0})}
                      min="0"
                      max="100000"
                      placeholder="0"
                      style={{ width: '100%', padding: '10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ padding: '12px 24px', background: '#f1f5f9', border: 'none', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '12px 24px', background: '#667eea', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontWeight: '600' }}
                >
                  {modalType === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={showPasswordResetModal}
        onClose={() => {
          setShowPasswordResetModal(false);
          setTemporaryPassword('');
          setResetPasswordUserEmail('');
        }}
        userEmail={resetPasswordUserEmail}
        temporaryPassword={temporaryPassword}
      />
      
      {/* Rate Limit Status Modal */}
      {showRateLimitModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            margin: '0 20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', background: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BarChart3 size={20} style={{ color: '#d97706' }} />
                </div>
                <div>
                  <h3 style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600', margin: 0 }}>Rate Limit Status</h3>
                  {rateLimitStatus && !rateLimitStatus.error && (
                    <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>{rateLimitStatus.email}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowRateLimitModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            {rateLimitLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                Loading...
              </div>
            ) : rateLimitStatus?.error ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                {rateLimitStatus.error}
              </div>
            ) : rateLimitStatus ? (
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Window</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Limit</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Used</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>Per Minute</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {rateLimitStatus.limits.per_minute === 'unlimited' ? '∞' : rateLimitStatus.limits.per_minute}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {rateLimitStatus.usage.minute}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        textAlign: 'center', 
                        fontWeight: '600',
                        borderBottom: '1px solid #f1f5f9',
                        color: rateLimitStatus.remaining.minute === 'unlimited' ? '#10b981' : 
                               (typeof rateLimitStatus.remaining.minute === 'number' && rateLimitStatus.remaining.minute <= 0) ? '#ef4444' : 
                               (typeof rateLimitStatus.remaining.minute === 'number' && rateLimitStatus.remaining.minute <= 3) ? '#f59e0b' : '#10b981'
                      }}>
                        {rateLimitStatus.remaining.minute === 'unlimited' ? '∞' : rateLimitStatus.remaining.minute}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: '500', borderBottom: '1px solid #f1f5f9' }}>Per Hour</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {rateLimitStatus.limits.per_hour === 'unlimited' ? '∞' : rateLimitStatus.limits.per_hour}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {rateLimitStatus.usage.hour}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        textAlign: 'center', 
                        fontWeight: '600',
                        borderBottom: '1px solid #f1f5f9',
                        color: rateLimitStatus.remaining.hour === 'unlimited' ? '#10b981' : 
                               (typeof rateLimitStatus.remaining.hour === 'number' && rateLimitStatus.remaining.hour <= 0) ? '#ef4444' : 
                               (typeof rateLimitStatus.remaining.hour === 'number' && rateLimitStatus.remaining.hour <= 5) ? '#f59e0b' : '#10b981'
                      }}>
                        {rateLimitStatus.remaining.hour === 'unlimited' ? '∞' : rateLimitStatus.remaining.hour}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '14px 16px', color: '#1e293b', fontWeight: '500' }}>Per Day</td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b' }}>
                        {rateLimitStatus.limits.per_day === 'unlimited' ? '∞' : rateLimitStatus.limits.per_day}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b' }}>
                        {rateLimitStatus.usage.day}
                      </td>
                      <td style={{ 
                        padding: '14px 16px', 
                        textAlign: 'center', 
                        fontWeight: '600',
                        color: rateLimitStatus.remaining.day === 'unlimited' ? '#10b981' : 
                               (typeof rateLimitStatus.remaining.day === 'number' && rateLimitStatus.remaining.day <= 0) ? '#ef4444' : 
                               (typeof rateLimitStatus.remaining.day === 'number' && rateLimitStatus.remaining.day <= 10) ? '#f59e0b' : '#10b981'
                      }}>
                        {rateLimitStatus.remaining.day === 'unlimited' ? '∞' : rateLimitStatus.remaining.day}
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                <div style={{ marginTop: '20px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px', color: '#64748b' }}>
                  <strong>Note:</strong> Counters reset based on UTC time. 
                  <br />• Minute: Every new minute
                  <br />• Hour: Every new UTC hour (X:30 IST)
                  <br />• Day: At midnight UTC (5:30 AM IST)
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRateLimitModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#64748b',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Modal */}
      {showSubscriptionModal && subscriptionUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            margin: '0 20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', background: '#ede9fe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CreditCard size={20} style={{ color: '#7c3aed' }} />
                </div>
                <div>
                  <h3 style={{ color: '#1e293b', fontSize: '18px', fontWeight: '600', margin: 0 }}>Manage Subscription</h3>
                  <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>{subscriptionUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            {subscriptionMessage.text && (
              <div style={{
                padding: '12px',
                background: subscriptionMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
                color: subscriptionMessage.type === 'success' ? '#166534' : '#991b1b',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}>
                {subscriptionMessage.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
                {subscriptionMessage.text}
              </div>
            )}

            {/* Current Status */}
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', marginBottom: '20px' }}>
              <h4 style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '12px' }}>
                Current Status
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Plan</p>
                  <p style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', margin: 0, textTransform: 'capitalize' }}>
                    {subscriptionUser.subscription_status?.plan_name || subscriptionUser.subscription_plan || 'Free'}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Status</p>
                  <p style={{ 
                    color: subscriptionUser.subscription_status?.is_active ? '#22c55e' : '#ef4444', 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    margin: 0 
                  }}>
                    {subscriptionUser.subscription_status?.is_active ? 'Active' : 'Expired'}
                  </p>
                </div>
                {subscriptionUser.subscription_status?.days_remaining !== null && 
                 subscriptionUser.subscription_status?.days_remaining !== undefined &&
                 subscriptionUser.subscription_plan !== 'free' && (
                  <>
                    <div>
                      <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Days Remaining</p>
                      <p style={{ 
                        color: subscriptionUser.subscription_status.days_remaining <= 7 ? '#ef4444' : '#1e293b', 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        margin: 0 
                      }}>
                        {subscriptionUser.subscription_status.days_remaining}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 4px 0' }}>Expires</p>
                      <p style={{ color: '#1e293b', fontSize: '14px', margin: 0 }}>
                        {subscriptionUser.subscription_end ? new Date(subscriptionUser.subscription_end).toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Plan Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Select Plan
              </label>
              <select
                value={subscriptionForm.plan}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, plan: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  color: '#1e293b',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                {subscriptionPlans.filter(p => p.is_active).map(plan => (
                  <option key={plan.id} value={plan.slug}>
                    {plan.name} - ₹{plan.price} ({plan.max_sessions} sessions, {plan.rate_limit_per_day === 0 ? 'unlimited' : plan.rate_limit_per_day} msg/day)
                  </option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#64748b', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Duration (days)
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[7, 30, 90, 365].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setSubscriptionForm({ ...subscriptionForm, duration_days: days })}
                    style={{
                      padding: '8px 16px',
                      border: `2px solid ${subscriptionForm.duration_days === days ? '#7c3aed' : '#e2e8f0'}`,
                      borderRadius: '8px',
                      background: subscriptionForm.duration_days === days ? '#ede9fe' : 'white',
                      color: subscriptionForm.duration_days === days ? '#7c3aed' : '#64748b',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}
                  >
                    {days}d
                  </button>
                ))}
                <input
                  type="number"
                  value={subscriptionForm.duration_days}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, duration_days: parseInt(e.target.value) || 30 })}
                  min="1"
                  style={{
                    width: '80px',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    color: '#1e293b',
                    textAlign: 'center'
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleActivateSubscription}
                disabled={subscriptionLoading}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: subscriptionLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: subscriptionLoading ? 0.6 : 1
                }}
              >
                {subscriptionLoading ? 'Processing...' : 'Activate Plan'}
              </button>
              
              {subscriptionUser.subscription_plan !== 'free' && subscriptionUser.subscription_status?.is_active && (
                <button
                  onClick={handleExtendSubscription}
                  disabled={subscriptionLoading}
                  style={{
                    padding: '12px 20px',
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: subscriptionLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: subscriptionLoading ? 0.6 : 1
                  }}
                >
                  Extend
                </button>
              )}
              
              {subscriptionUser.subscription_plan !== 'free' && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={subscriptionLoading}
                  style={{
                    padding: '12px 20px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: subscriptionLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: subscriptionLoading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
